import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type MeResponse } from '../api/client'
import { describeError } from '../lib/describeError'
import type { ThemePref } from '../lib/theme'

const OPTIONS: { value: ThemePref; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'Match my computer' },
]

/** Screen style, saved per person. Optimistic: the page re-themes at once and
 *  rolls back (with the reason) if the save fails. */
export function ThemeSwitch({ value }: { value: ThemePref }) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (editorial_theme: ThemePref) => api.patch<{ editorial_theme: ThemePref }>('/api/me/preferences', { editorial_theme }),
    onMutate: async (editorial_theme) => {
      setError(null)
      await qc.cancelQueries({ queryKey: ['me'] })
      const previous = qc.getQueryData<MeResponse>(['me'])
      if (previous) qc.setQueryData<MeResponse>(['me'], { ...previous, preferences: { ...previous.preferences, editorial_theme } })
      return { previous }
    },
    onError: (err, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(['me'], ctx.previous)
      setError(`Could not save your screen style: ${describeError(err)}`)
    },
    onSettled: () => { qc.invalidateQueries({ queryKey: ['me'] }) },
  })

  return (
    <div className="flex flex-col gap-1.5">
      <span id="screen-style-label" className="text-[12px] text-paper-faint">Screen style</span>
      <div role="radiogroup" aria-labelledby="screen-style-label" className="flex flex-col gap-1">
        {OPTIONS.map((o) => {
          const on = value === o.value
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => { if (!on) save.mutate(o.value) }}
              className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow ${on ? 'bg-ink-600 font-semibold text-paper' : 'text-paper-dim hover:text-paper'}`}
            >
              <span aria-hidden className={`h-3 w-3 shrink-0 rounded-full border ${on ? 'border-amber-glow bg-amber-glow' : 'border-paper-faint'}`} />
              {o.label}
            </button>
          )
        })}
      </div>
      {error && <p role="alert" className="text-[12px] text-radar-red">{error}</p>}
    </div>
  )
}
